import { getIn, useFormikContext } from "formik"
import { useMemo, useState, useEffect } from "react"
import { ImCheckboxChecked, ImCheckboxUnchecked, ImWarning } from "react-icons/im"
import { UploadDocumentFieldFormik } from "../../renters/components/forms/fields/UploadDocumentFieldFormik"
import { UploadImageField } from "../../../common/components/form/UploadImageField/UploadImageField"
import { AvailableDocumentEventClaimTypesFormik } from "./AvailableDocumentEventClaimTypesFormik"
import { EventDocuments } from "../../../types/eventClaim.types"
import { FileInputPdf } from "../../../common/components/form/FileInputPdf"
import { useToastContext } from "../../../common/components/ui/toast/toast.context"

type DocumentRowProps = {
  name: string
  initialData?: Partial<{ type: string, content: string, verified: boolean }> | EventDocuments
}

export function DocumentEventClaimRow(props: DocumentRowProps) {
  const { values, setFieldValue } = useFormikContext();
  const eventId = getIn(values, '_id') || 'temp';
  const theDoc = getIn(values, props.name) || {};
  const currentType = getIn(values, `${props.name}.type`) || props.initialData?.type;
  const pathToUpload = useMemo(() => `events/${eventId}`, [eventId]);
  const [isFileContentPdf, setIsFileContentPdf] = useState(false);
  const { notifySuccess } = useToastContext();
  
  // Verifica se o arquivo já existente é um PDF
  useEffect(() => {
    if (theDoc?.content) {
      // Verifica se o conteúdo tem extensão ou nome que indique PDF
      const isPdf = 
        theDoc.content.toLowerCase().endsWith('.pdf') || 
        theDoc.content.toLowerCase().includes('/pdf/') ||
        theDoc.content.toLowerCase().includes('application/pdf');
      
      setIsFileContentPdf(isPdf);
    } else {
      setIsFileContentPdf(false);
    }
  }, [theDoc?.content]);
  
  // Função para lidar com o upload de PDF
  const handlePdfUpload = (filePath: string) => {
    setFieldValue(`${props.name}.content`, filePath);
    notifySuccess('PDF enviado com sucesso');
  };

  return (
    <div className="document-row grid grid-cols-1 md:grid-cols-5 items-center ">
      <AvailableDocumentEventClaimTypesFormik
        className="md:col-span-3 mb-2 md:mb-0 p-[8px]"
        name={`${props.name}.type`}
      />

      {/* Renderização condicional baseada no tipo de arquivo */}
      <div className="flex items-center md:col-start-4">
        {isFileContentPdf ? (
          // Render FileInputPdf para documentos PDF
          <FileInputPdf
            onChange={handlePdfUpload}
            pathToUpload={pathToUpload}
            currentPath={theDoc?.content || ''}
            nameFile="Enviar PDF"
          />
        ) : (
          // Render UploadDocumentFieldFormik para imagens
          <UploadDocumentFieldFormik 
            name={`${props.name}.content`} 
            pathToUpload={pathToUpload}
            uploadName={currentType}
          >
            {({ src, onNewFile, onRemoveClick }) => (
              <div className="flex flex-col items-center justify-center p-[5px]">
                <UploadImageField
                  src={src}
                  onNewFile={(file) => {
                    // Verifica se o novo arquivo é um PDF
                    if (file && file.type === 'application/pdf') {
                      // Se for PDF, cancela este upload e usa o FileInputPdf
                      setIsFileContentPdf(true);
                      // Simulando um clique no botão de PDF
                      setTimeout(() => {
                        const pdfButtons = document.querySelectorAll('button');
                        const pdfButton = Array.from(pdfButtons).find(
                          btn => btn.textContent === 'Enviar PDF'
                        );
                        pdfButton?.click();
                      }, 100);
                    } else {
                      // Se for imagem, continua normalmente
                      onNewFile(file);
                    }
                  }}
                  onRemoveClick={onRemoveClick}
                />
                {!isFileContentPdf && theDoc?.content && (
                  <button
                    type="button" 
                    className="text-blue-500 text-xs mt-1 hover:underline"
                    onClick={() => setIsFileContentPdf(true)}
                  >
                    Mudar para upload de PDF
                  </button>
                )}
              </div>
            )}
          </UploadDocumentFieldFormik>
        )}
      </div>

      <div className="md:col-start-5 md:self-start">
        <button
          onClick={() => setFieldValue(`${props.name}.verified`, !theDoc.verified)}
          type="button"
          className="w-full bg-gray/5 hover:bg-gray/10 p-2 rounded"
          disabled={!theDoc.content}
        >
          {!theDoc.content ? (
            <div className="flex gap-2 items-center justify-start text-yellowDark cursor-pointer">
              <ImWarning />
              <span className="text-brown" style={{ lineHeight: '.9' }}>Adicione o arquivo</span>
            </div>
          ) : (theDoc.verified ? (
            <div className="flex gap-2 items-center justify-start text-green">
              <ImCheckboxChecked />
              <span>Aprovado</span>
            </div>
          ) : (
            <div className="flex gap-2 items-center justify-start text-red">
              <ImCheckboxUnchecked />
              <span>Não aprovado</span>
            </div>
          ))}
        </button>
      </div>
    </div>
  );
}
